import React, { useEffect, useMemo, useRef } from "react";
import { PleaseWaitSpinner } from "../../../../jsxctl/utilctl";
import TranslateManager from 'ts-translate-js';
import './promo-code-input-control.scss';
import { useIFrameResize } from "../../../logic/hooks/utils";
import { AddToCartPackageRequest, AddToCartRequest, AddToCartRequestBase } from "../../../mvcapi/models/buyticket/ticketselection";
import { PriceChangeSuggestion } from "../../../mvcapi/models/buyticket";
import toastr from 'toastr';

const tr = TranslateManager.boundTR;

type PromoCodeInputControlProps = {
    promoKind: number;
    promoID: string;
    promoDefGroupingIDs: string[];
    title?: string;
    setInputFocus?: boolean;
    onPromoUpdated: () => void;
    onContentChange: () => void;
    addToCartRequest?: AddToCartRequestBase; // Optional. Used for price change suggestion.
    onPriceChangeSuggestions?: (priceChangeSuggestions: PriceChangeSuggestion[]) => void; // Optional. Used for price change suggestion.
    isAPackageSale: boolean;
}

export function PromoCodeInputControl({promoKind, promoID, promoDefGroupingIDs, onPriceChangeSuggestions, title, addToCartRequest, setInputFocus = false, onPromoUpdated, onContentChange, isAPackageSale}: PromoCodeInputControlProps) {
    const [ password, setPassword ] = React.useState("");
    const [ checkingPassword, setCheckingPassword ] = React.useState(false);
    const [ emailMismatch, setEmailMismatch ] = React.useState(false);
    const [ hasError, setHasError ] = React.useState(false);
    const [ error, setError ] = React.useState("");

    useIFrameResize();

    const promoPasswordInputFieldRef = useRef(null);

    const ERR_BADPASSWORD = "BADPASSWORD";
    const ERR_UNKNOWN = "UNKNOWN";

    useEffect(() => {
        if(setInputFocus && promoPasswordInputFieldRef.current)
            promoPasswordInputFieldRef.current.focus();
    }, [setInputFocus]);

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        setEmailMismatch(false);
        setHasError(false);
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault(); 
        e.stopPropagation();

        if(password.length === 0) {
            toastr.warning(tr("Veuillez entrer un code promo"));
            return;
        }

        setCheckingPassword(true);
        setHasError(false);

        try {
            let passwordResult;
            if(isAPackageSale) {
                passwordResult = await tsapp.mvcapi.postCtl("buypackage/activatepromobyid", {
                    promokind: promoKind,
                    promoid: promoID,
                    password: password,
                    promoDefGroupingIDs: promoDefGroupingIDs,
                    addToCartRequest: addToCartRequest as AddToCartPackageRequest,
                });
            }
            else {
                passwordResult = await tsapp.mvcapi.postCtl("buyticket/activatepromobyid", {
                    promokind: promoKind,
                    promoid: promoID,
                    password: password,
                    promoDefGroupingIDs: promoDefGroupingIDs,
                    addToCartRequest: addToCartRequest as AddToCartRequest,
                });
            }

            if(passwordResult.priceChangeSuggestions && onPriceChangeSuggestions)
                onPriceChangeSuggestions(passwordResult.priceChangeSuggestions);

            if(passwordResult.isSuccess) {
                if(onPromoUpdated) 
                    onPromoUpdated(); 
            }
            else {
                setHasError(true);
                setEmailMismatch(passwordResult.emailMismatch);
                setError(passwordResult.invalidPassword ? ERR_BADPASSWORD : ERR_UNKNOWN);
            }
        }
        catch(err) {
            setHasError(true);
            setEmailMismatch(err.emailMismatch);
            setError(err.invalidPassword ? ERR_BADPASSWORD : ERR_UNKNOWN);
        }

        setCheckingPassword(false);
    };

    const errorContent = useMemo(() => {
        let content;

        if ((hasError || emailMismatch) && onContentChange != undefined) {
            onContentChange();
        }

        if(hasError) {
            content = <div className="alert alert-danger">{error === ERR_BADPASSWORD 
                ? tr("Code incorrect") 
                : tr("Une erreur est survenue")}
            </div>;
        }

        if(emailMismatch) {
            content = <div>
                <div className="alert alert-danger">{tr("Courriel invalide")}</div>
                <div className="alert alert-info">
                    {tr("L'adresse courriel associée à ce code ne correspond pas à l'adresse courriel de votre profil. Veuillez vous déconnecter et essayer de nouveau.")}
                </div>
            </div>;
        }
        return content;
    }, [hasError, error, emailMismatch, onContentChange]);

    return <div className="promo-code-input-control">
        <div>
            <label htmlFor="promoPasswordInputField">{title}</label>
        </div>
        <form onSubmit={handlePasswordSubmit}>
            <div className="flex-inline center">
                <div>
                    <input ref={promoPasswordInputFieldRef} id="promoPasswordInputField" className="form-control" type="password" autoComplete="off" onChange={handlePasswordChange} value={password} placeholder={tr("code promo")} />
                </div>
                <div>
                    <button className="btn btn-primary text-uppercase form-inline-button" type="submit">{tr("Valider")}</button>
                </div>
            </div>
            <div>
                {checkingPassword && <PleaseWaitSpinner />}
                {errorContent}
            </div>
        </form>
    </div>;
}